import { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from '../contexts/AppContext';
import { ReadablePalette } from '../common/Color';
import { getImageUrl } from '../common/Media';
import { u } from '../common/Units';
import { MEDIA_PREFIX } from '../Settings';
import Loader from './Loader';
import { DMExperience, fetchDMExperiences, getDMImageUrl, getDMVirtualExperienceUrl } from '../services/DigitaltMuseum';
import VMIcon from '../assets/icon_vm.svg?react';

interface DMVirtualExperienceProps {
  /** DM artifact UUID of the virtual experience (vm-exhibition) */
  uuid: string,
  /** Owner code (identifier.owner). Decides the DM domain: .no/.se only show their own country's owners */
  ownerCode?: string,
  /** Title of the virtual experience */
  title?: string,
  palette: ReadablePalette,
  /**
   * "fullscreen" shows a bar with room for the close button of the parent, and a button back to the menu.
   * "page" shows no bar: the selected experience is stored in the URL (?experience=), so the kiosk's
   * own back button returns to the menu
   */
  variant?: "fullscreen" | "page",
  /** Open this experience (content ID) directly, e.g. when a part of the experience was opened from a list */
  contentId?: string
}

const Wrapper = styled.div<{ $palette: ReadablePalette }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  color: ${p => p.$palette.textColor};
`;

const Bar = styled.div`
  flex: 0 0 ${u(100)};
  display: flex;
  align-items: center;
  gap: ${u(16)};
  padding: 0 ${u(110)} 0 ${u(30)};
  box-sizing: border-box;
  font-size: 1.1em;
  font-weight: 600;

  .page & {
    flex-basis: ${u(80)};
    padding: 0;
  }

  & > svg {
    width: ${u(28)};
    height: ${u(31)};
    flex: 0 0 auto;
  }

  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const BackButton = styled.button<{ $palette: ReadablePalette }>`
  height: ${u(48)};
  padding: 0 ${u(20)};
  border-radius: ${u(24)};
  border: 1px solid ${p => p.$palette.borderColor};
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.8em;
  font-weight: normal;
  white-space: nowrap;
  cursor: pointer;
`;

/**
 * DM shows its own app bar (DM logo, title and "Avslutt") on top of an experience. It links to the rest of
 * DigitaltMuseum, so it is cropped away by moving the iframe up. The bar is 64px (sticky) in all sizes and
 * experience types tested (quiz, scrollytell, 3droom). Only cropped when a specific experience is open.
 */
const DM_APP_BAR_HEIGHT = 64;
/** Cropping of DM's app bar is turned off for now. Set to true to hide the bar again */
const CROP_DM_APP_BAR = false;

const FrameWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  background: #fff;

  .page & {
    border-radius: ${u(8)};
  }
`;

const Frame = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;

  &.cropped {
    top: -${DM_APP_BAR_HEIGHT}px;
    height: calc(100% + ${DM_APP_BAR_HEIGHT}px);
  }
`;

const Menu = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${u(20)} ${u(30)} ${u(40)};

  .page & {
    padding: ${u(10)} 0 ${u(40)};
  }
`;

/**
 * Number of columns in the menu, chosen so the rows are as even as possible:
 * 2 → 2, 3 → 3, 4 → 2+2, 5 → 3+2, 6 → 3+3, 7 → 4+3, 8 → 4+4, 9 → 3+3+3, more → 4
 * @param {number} count Number of experiences
 * @returns {number} Columns
 */
const getMenuColumns = (count: number): number => {
  if (count <= 3) { return Math.max(count, 1); }
  if (count === 4) { return 2; }
  if (count === 7 || count === 8) { return 4; }
  if (count <= 9) { return 3; }
  return 4;
}

const MENU_GAP = 24;

/** Flex layout, so an incomplete last row is centered instead of left aligned */
const MenuGrid = styled.ul<{ $columns: number }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${u(MENU_GAP)};

  & > li {
    flex: 0 0 calc((100% - ${p => p.$columns - 1} * ${u(MENU_GAP)}) / ${p => p.$columns});
  }

  @media (orientation: portrait) {
    & > li {
      flex-basis: calc((100% - ${p => Math.min(p.$columns, 2) - 1} * ${u(MENU_GAP)}) / ${p => Math.min(p.$columns, 2)});
    }
  }
`;

const MenuItem = styled.li<{ $palette: ReadablePalette }>`
  border-radius: ${u(16)};
  overflow: hidden;
  background-color: ${p => p.$palette.cardColor};
  cursor: pointer;

  &:active {
    transform: scale(0.98);
  }

  & .image {
    position: relative;
    padding-top: 56%;
    background-color: ${p => p.$palette.imageBackgroundColor};
  }

  & img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  & .text {
    padding: ${u(18)} ${u(20)} ${u(22)};
  }

  & .type {
    margin: 0 0 ${u(6)};
    font-size: 0.7em;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${p => p.$palette.mutedTextColor};
  }

  & h3 {
    margin: 0 0 ${u(8)};
    font-size: 1.05em;
  }

  & p.ingress {
    margin: 0;
    font-size: 0.85em;
    line-height: 1.4;
    color: ${p => p.$palette.mutedTextColor};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
  }
`;

/**
 * Show a DigitaltMuseum virtual experience in an iframe. Opens the experience directly
 * (?experienceId=...) when there is one, and shows a simple menu when there are several.
 * Falls back to DM's landing page for the virtual experience if the experiences can not be loaded.
 * @returns {JSX.Element} Component template
 */
const DMVirtualExperience: FC<DMVirtualExperienceProps> = ({ uuid, ownerCode, title, palette, variant = "fullscreen", contentId }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [experiences, setExperiences] = useState<DMExperience[] | null>(null);
  const [selectedState, setSelectedState] = useState<DMExperience | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const isPage = variant === "page";
  const baseUrl = getDMVirtualExperienceUrl(uuid, currentLanguage, ownerCode);

  // Get the experiences. Open directly if there is only one
  useEffect(() => {
    let isSubscribed = true;
    setExperiences(null);
    setSelectedState(null);
    setHasError(false);

    fetchDMExperiences(uuid).then(result => {
      isSubscribed && setExperiences(result);
    }).catch(() => {
      isSubscribed && setHasError(true);
    });

    return () => { isSubscribed = false };
  }, [uuid]);

  const hasMenu = (experiences?.length || 0) > 1;

  // Selected experience. A single experience is always selected. In the page variant, the selection is read from the URL
  const selectedId = (isPage ? searchParams.get("experience") : selectedState?.contentId) || contentId;
  const selected = experiences?.length === 1
    ? experiences[0]
    : experiences?.find(experience => experience.contentId === selectedId) || null;

  /**
   * Select an experience. In the page variant a history entry is added, so "back" returns to the menu
   */
  const selectExperience = (experience: DMExperience | null) => {
    isPage ? setSearchParams(experience ? { experience: experience.contentId } : {}) : setSelectedState(experience);
  };

  // Falls back to the landing page if the experiences are missing
  const src = selected
    ? `${baseUrl}?experienceId=${encodeURIComponent(selected.contentId)}`
    : (hasError || experiences?.length === 0) ? baseUrl : undefined;

  return (
    <Wrapper $palette={palette} className={variant}>
      {!isPage && (
        <Bar>
          {hasMenu && selected && <BackButton $palette={palette} onClick={() => selectExperience(null)}>{t("dm.vm.back")}</BackButton>}
          <VMIcon/>
          <span>{selected?.name || title}</span>
        </Bar>
      )}

      {src && (
        <FrameWrapper>
          <Frame key={src} className={selected && CROP_DM_APP_BAR ? "cropped" : undefined} src={src} title={selected?.name || title || ""} sandbox="allow-scripts allow-same-origin allow-forms" allow="autoplay; fullscreen; xr-spatial-tracking; gyroscope; accelerometer"/>
        </FrameWrapper>
      )}

      {!src && !experiences && <Loader variant="fill" color={palette.mutedTextColor}/>}

      {!src && hasMenu && (
        <Menu>
          <MenuGrid $columns={getMenuColumns(experiences?.length || 0)}>
            {experiences?.map(experience => (
              <MenuItem key={experience.contentId} $palette={palette} role="button" tabIndex={0} onClick={() => selectExperience(experience)}>
                <div className="image">
                  {experience.previewDmsId && <img src={`${MEDIA_PREFIX}${getImageUrl(getDMImageUrl(experience.previewDmsId), { dimension: "600x600" })}`} alt=""/>}
                </div>
                <div className="text">
                  {experience.type && <p className="type">{t(`dm.vm.type.${experience.type}`, { defaultValue: experience.type })}</p>}
                  <h3>{experience.name}</h3>
                  {experience.ingress && <p className="ingress">{experience.ingress}</p>}
                </div>
              </MenuItem>
            ))}
          </MenuGrid>
        </Menu>
      )}
    </Wrapper>
  );
}

export default DMVirtualExperience;
